<script setup lang="ts">
import { asText } from '@prismicio/client'
import type { Content } from '@prismicio/client'

defineProps(getSliceComponentProps<Content.VacancyGridSlice>())

const { client } = usePrismic()
const { data: vacancies } = await useAsyncData('vacancies-grid', async () => {
  const all = await client.getAllByType('vacancy', {
    orderings: [{ field: 'my.vacancy.order' }],
  })
  return all.filter((v) => !v.data.hide_in_grid)
})

// Active filter values — null means "Alle" (no filter on that dimension).
const filters = reactive<Record<'type' | 'level', string | null>>({
  type: null,
  level: null,
})

// Canonical order so the derived filter chips render predictably.
const TYPE_ORDER = ['Fulltime', 'Parttime', 'Fulltime / Parttime', 'Stage', 'Bijbaan', 'ZZP / Freelance']
const LEVEL_ORDER = ['Leerling', 'Beginnend', 'Ervaren']

// Only show chips for values that actually exist in the published vacancies,
// ordered by the canonical arrays above (unknown values appended at the end).
function distinct(get: (d: Record<string, any>) => string | null | undefined, order: string[]) {
  const present = new Set<string>()
  for (const v of vacancies.value ?? []) {
    const val = get(v.data as Record<string, any>)
    if (val) present.add(val)
  }
  const known = order.filter((o) => present.has(o))
  const extra = [...present].filter((p) => !order.includes(p))
  return [...known, ...extra]
}

const groups = computed(() => [
  { key: 'type' as const, label: 'Dienstverband', options: distinct((d) => d.employment_type, TYPE_ORDER) },
  { key: 'level' as const, label: 'Niveau', options: distinct((d) => d.level, LEVEL_ORDER) },
])

const filtered = computed(() =>
  (vacancies.value ?? []).filter((v) => {
    const d = v.data as Record<string, any>
    if (filters.type && d.employment_type !== filters.type) return false
    if (filters.level && d.level !== filters.level) return false
    return true
  }),
)
</script>

<template>
  <section
    :data-slice-type="slice.slice_type"
    :data-slice-variation="slice.variation"
    :class="['pa-vacancy-grid', 'pa-vacancy-grid--' + slice.variation]"
  >
    <div class="pa-container">
      <div class="pa-vacancy-grid__header">
        <div>
          <div v-if="slice.primary.eyebrow" class="pa-vacancy-grid__eyebrow">
            {{ slice.primary.eyebrow }}
          </div>
          <div class="pa-vacancy-grid__heading">
            <PrismicRichText :field="slice.primary.heading" />
          </div>
        </div>
        <div class="pa-vacancy-grid__count">
          {{ filtered.length }} {{ filtered.length === 1 ? 'vacature' : 'vacatures' }}
        </div>
      </div>

      <div class="pa-vacancy-grid__filters">
        <div
          v-for="g in groups"
          :key="g.key"
          class="pa-vacancy-grid__filter-group"
        >
          <span class="pa-vacancy-grid__filter-label">{{ g.label }}</span>
          <button
            type="button"
            class="pa-vacancy-grid__pill"
            :class="{ 'is-active': filters[g.key] === null }"
            @click="filters[g.key] = null"
          >
            Alle
          </button>
          <button
            v-for="opt in g.options"
            :key="opt"
            type="button"
            class="pa-vacancy-grid__pill"
            :class="{ 'is-active': filters[g.key] === opt }"
            @click="filters[g.key] = opt"
          >
            {{ opt }}
          </button>
        </div>
      </div>

      <p v-if="!filtered.length" class="pa-vacancy-grid__empty">
        {{ slice.primary.empty_text || 'Er zijn op dit moment geen openstaande vacatures.' }}
      </p>

      <div v-else class="pa-vacancy-grid__grid">
        <NuxtLink
          v-for="vacancy in filtered"
          :key="vacancy.id"
          :to="vacancy.url || `/vacatures/${vacancy.uid}`"
          class="pa-vacancy-grid__card"
        >
          <div class="pa-vacancy-grid__card-image">
            <PrismicImage
              v-if="vacancy.data.featured_image?.url"
              :field="vacancy.data.featured_image"
              :widths="[400, 600, 800]"
              sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
              :imgix-params="{ ar: '4:3', fit: 'crop', auto: 'format,compress' }"
            />
          </div>

          <div class="pa-vacancy-grid__card-body">
            <ul class="pa-vacancy-grid__card-badges">
              <li v-if="vacancy.data.employment_type" class="pa-vacancy-grid__badge">
                {{ vacancy.data.employment_type }}
              </li>
              <li v-if="vacancy.data.location" class="pa-vacancy-grid__badge">
                {{ vacancy.data.location }}
              </li>
              <li
                v-if="vacancy.data.level"
                class="pa-vacancy-grid__badge pa-vacancy-grid__badge--level"
              >
                {{ vacancy.data.level }}
              </li>
            </ul>

            <h3 class="pa-vacancy-grid__card-title">
              {{ asText(vacancy.data.title) }}
            </h3>

            <div v-if="vacancy.data.excerpt?.length" class="pa-vacancy-grid__card-excerpt">
              <PrismicRichText :field="vacancy.data.excerpt" />
            </div>

            <div class="pa-vacancy-grid__card-footer">
              <span v-if="vacancy.data.hours_per_week" class="pa-vacancy-grid__card-hours">
                {{ vacancy.data.hours_per_week }}
              </span>
              <span class="pa-vacancy-grid__card-link">Bekijk vacature &rarr;</span>
            </div>
          </div>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
